import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { CaregiverCompactDto, CaregiverCompactPageFilter } from '../../models/caregiver.model';
import { CaregiverService } from '../../../services/caregiver.service';
import { CompanyService } from '../../../services/company.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { FilterOperator } from 'primeng/api';
import { Subscription, Subject, debounceTime, distinctUntilChanged, switchMap, of } from 'rxjs';

@Component({
  selector: 'app-caregiver-picker-compact',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './caregiver-picker-compact.component.html',
  styleUrls: ['./caregiver-picker-compact.component.scss']
})
export class CaregiverPickerCompactComponent implements OnInit, OnDestroy, OnChanges {
  private caregiverService = inject(CaregiverService);
  private companyService = inject(CompanyService);

  caregivers: CaregiverCompactDto[] = [];
  isLoading = false;
  selectedCaregiver: CaregiverCompactDto | null = null;
  selectedCompanyId: string | null = null;

  private companySubscription: Subscription | null = null;
  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription | null = null;

  @Input() value: CaregiverCompactDto | null = null;
  @Input() selectedCaregiverId: string | null = null;
  @Input() clearTrigger = false;
  @Input() placeholder = 'Select a caregiver';
  @Input() showClear = true;
  @Input() size: 'small' | 'large' = 'small';
  @Input() disabled = false;
  @Output() valueChange = new EventEmitter<CaregiverCompactDto | null>();

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.setupCompanySubscription();
    this.initializeSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clearTrigger']?.currentValue) {
      this.clear();
    }

    if (changes['value'] && !changes['value'].firstChange) {
      this.selectedCaregiver = changes['value'].currentValue;
    }

    if (changes['selectedCaregiverId'] && !changes['selectedCaregiverId'].firstChange) {
      this.updateSelectionFromId(changes['selectedCaregiverId'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.companySubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
  }

  private setupCompanySubscription(): void {
    this.companySubscription = this.companyService.getSelectedCompanySkey().subscribe((sKey) => {
      const newCompanyId = sKey || null;
      if (this.selectedCompanyId !== newCompanyId) {
        this.selectedCompanyId = newCompanyId;
        // Only fully reset if we don't have a pre-selected caregiver ID
        // If we have a selectedCaregiverId, preserve the selection intent
        if (!this.selectedCaregiverId) {
          this.resetComponent();
        } else {
          // Just clear the caregivers list but preserve the selection intent
          this.caregivers = [];
          this.isLoading = false;
        }
        if (this.selectedCompanyId) {
          this.loadInitialCaregivers();
          // If we have a selectedCaregiverId, re-fetch the caregiver after company change
          if (this.selectedCaregiverId) {
            this.fetchCaregiverById(this.selectedCaregiverId);
          }
        }
      }
    });
  }

  private setupSearchDebounce(): void {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(600),
        distinctUntilChanged(),
        switchMap((searchTerm) => this.searchCaregiversObservable(searchTerm))
      )
      .subscribe({
        next: (result) => {
          this.handleSearchResult(result);
        },
        error: () => {
          this.isLoading = false;
          this.caregivers = [];
        }
      });
  }

  private initializeSelection(): void {
    if (this.value) {
      this.selectedCaregiver = this.value;
    } else if (this.selectedCaregiverId) {
      // Try to find in existing caregivers first, otherwise fetch by ID
      const existingCaregiver = this.caregivers.find((c) => c.sKey === this.selectedCaregiverId);
      if (existingCaregiver) {
        this.selectedCaregiver = existingCaregiver;
        this.valueChange.emit(existingCaregiver);
      } else {
        // Fetch the caregiver by ID directly - don't wait for initial list
        this.fetchCaregiverById(this.selectedCaregiverId);
      }
    }
  }

  private resetComponent(): void {
    this.caregivers = [];
    this.selectedCaregiver = null;
    this.isLoading = false;
  }

  private handleSearchResult(result: any): void {
    this.isLoading = false;
    if (result?.isSuccess) {
      this.caregivers = (result.data?.list || []).map((caregiver: any) => ({
        ...caregiver,
        displayName: this.getCaregiverDisplay(caregiver)
      }));

      // If we have a selectedCaregiverId but no selectedCaregiver, try to find it
      if (this.selectedCaregiverId && !this.selectedCaregiver) {
        this.updateSelectionFromId(this.selectedCaregiverId);
      }
    } else {
      this.caregivers = [];
    }
  }

  private searchCaregiversObservable(searchTerm: string) {
    if (!this.selectedCompanyId) {
      this.caregivers = [];
      this.isLoading = false;
      return of({ isSuccess: true, data: { list: [] } });
    }

    this.isLoading = true;

    const searchFilters = this.buildSearchFilters(searchTerm);
    const sorts = [{ field: 'lastUpdate', order: -1 }];

    const params: CaregiverCompactPageFilter = {
      pageNumber: 1,
      pageSize: 10,
      sorts: sorts,
      filters: searchFilters,
      companyId: this.selectedCompanyId
    };

    return this.caregiverService.fetchCaregiversCompact(params);
  }

  private buildSearchFilters(searchTerm: string): any[] {
    if (!searchTerm.trim()) {
      return [];
    }

    const words = searchTerm.trim().split(/\s+/);

    if (words.length === 2) {
      // Two words: treat as firstName and lastName
      return [
        {
          field: 'firstName',
          value: words[0],
          matchMode: 'contains',
          operator: FilterOperator.AND
        },
        {
          field: 'lastName',
          value: words[1],
          matchMode: 'contains',
          operator: FilterOperator.AND
        }
      ];
    } else {
      // Single word or more than 2 words: search in both fields with OR
      return [
        {
          field: 'firstName',
          value: searchTerm,
          matchMode: 'contains',
          operator: FilterOperator.OR
        },
        {
          field: 'lastName',
          value: searchTerm,
          matchMode: 'contains',
          operator: FilterOperator.OR
        }
      ];
    }
  }

  loadInitialCaregivers(): void {
    // Trigger search with empty term to load initial data
    this.searchSubject.next('');
  }

  private updateSelectionFromId(caregiverId: string | null): void {
    if (!caregiverId) {
      this.selectedCaregiver = null;
      this.valueChange.emit(null);
      return;
    }

    const caregiver = this.caregivers.find((c) => c.sKey === caregiverId);
    if (caregiver) {
      this.selectedCaregiver = caregiver;
      this.valueChange.emit(caregiver);
    } else {
      // Caregiver not found in current list, fetch by ID
      this.fetchCaregiverById(caregiverId);
    }
  }

  private fetchCaregiverById(caregiverId: string): void {
    this.isLoading = true;
    this.caregiverService.getCaregiverDetailsById(caregiverId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response?.isSuccess && response.data) {
          const data = response.data;
          const caregiver = {
            _id: data._id || data.sKey,
            companyId: data.companyId,
            sKey: data.sKey,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            isDeActivated: data.isDeActivated ?? false,
            isDESynced: data.isDESynced ?? false,
            editableTimePermission: data.editableTimePermission ?? true,
            employeeId: data.employeeId || '',
            displayName: this.getCaregiverDisplay(data)
          };
          // Add to caregivers list if not already present
          if (!this.caregivers.find((c) => c.sKey === caregiver.sKey)) {
            this.caregivers = [caregiver, ...this.caregivers];
          }
          this.selectedCaregiver = caregiver;
          this.valueChange.emit(caregiver);
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onFilter(event: { filter: string }): void {
    const searchTerm = event.filter || '';
    this.searchSubject.next(searchTerm);
  }

  onChange(caregiver: CaregiverCompactDto | null): void {
    this.selectedCaregiver = caregiver;
    this.valueChange.emit(this.selectedCaregiver);
  }

  getCaregiverDisplay(caregiver: any): string {
    if (!caregiver) return '';
    let display = `${caregiver.firstName} ${caregiver.lastName}`;
    if (caregiver.employeeId) {
      display += ` (ID: ${caregiver.employeeId})`;
    }
    if (caregiver.isDeActivated) {
      display += ' (deactivated)';
    }
    return display;
  }

  clear(): void {
    this.selectedCaregiver = null;
    this.valueChange.emit(null);
  }
}
